import { apiRequest } from "@/lib/queryClient";
import { QueryError } from "@/components/QueryError";
import { companyHref } from "@/lib/entityRoutes";
import { togglePageSelection, deleteContacts, loadSelectedContacts, loadFilteredContacts } from "@/lib/contactBulk";
import { useEffect, useState } from "react";
import { ListPager, type PageSize } from "@/components/data-grid/ListPager";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Mail,
  Copy,
  Trash2,
  Loader2,
  Download,
  X,
  CheckCircle2,
  Check,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AddContactModal } from "@/components/add/AddContactModal";
import { ImportDialog } from "@/components/import/ImportDialog";
import { EmptyState } from "@/components/EmptyState";
import { MobileContactCard, MobileContactCardSkeleton } from "@/components/mobile";
import { AirtableGrid, GridToolbar, type Column } from "@/components/data-grid";
import type { RowAction } from "@/components/data-grid/AirtableGrid";
import { ListShell } from "@/components/list/ListShell";
import { ListTiles } from "@/components/list/ListTiles";
import { ChartChips, ALL_KEY } from "@/components/list/ChartChips";
import { NameCell } from "@/components/list/NameCell";
import { contactTiles, contactCategoryChips, type ContactStats } from "@/components/list/tiles/contactTiles";

interface Contact {
  /** Joined in by the paged endpoint. */
  companyName?: string | null;
  companyCategory?: string | null;
  id: string;
  companyId: string;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  notes: string | null;
  createdAt: string;
  category?: string;
  /** Derived server-side: any call, meeting or conference involving this person. */
  engaged?: boolean;
  lastEngagementAt?: string | null;
  lastEngagementTitle?: string | null;
}

const getCategoryBadgeStyle = (category: string) => {
  switch (category) {
    case "Buyer":
      return "bg-secondary text-secondary-foreground";
    case "Client":
      return "gold-gradient text-primary-foreground";
    case "COI":
      return "bg-transparent border-2 border-secondary text-secondary font-semibold";
    default:
      return "bg-muted text-muted-foreground";
  }
};

export default function Contacts() {
  const [, navigate] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [scanCardOpen, setScanCardOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [copiedEmailId, setCopiedEmailId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fifty at a time, filtered in SQL, with the company joined in: this page
  // used to download every contact and every company on each visit.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300); return () => clearTimeout(t); }, [searchQuery]);
  useEffect(() => { setPage(1); }, [debouncedSearch, selectedCategory, pageSize]);

  const { data: pageData, isLoading: contactsLoading, isFetching, error: contactsError, refetch: retryContacts } = useQuery<{ contacts: Contact[]; pagination: { page: number; limit: number; total: number; totalPages: number; hasMore: boolean } }>({
    queryKey: ["contacts", "page", { page, pageSize, search: debouncedSearch, category: selectedCategory }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(pageSize));
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (selectedCategory !== "All Categories") params.set("category", selectedCategory);
      const res = await apiRequest("GET", `/api/contacts?${params.toString()}`);
      return res.json();
    },
    placeholderData: (prev) => prev,
  });
  const contacts = pageData?.contacts ?? [];
  const pagination = pageData?.pagination;

  // Follows the debounced search, like the paged list, so the tiles and chips describe what the box is showing.
  const { data: statsData } = useQuery<ContactStats>({
    queryKey: ["contacts", "stats", debouncedSearch],
    queryFn: async () => {
      const res = await fetch(`/api/contacts/stats?search=${encodeURIComponent(debouncedSearch)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch contact stats");
      return res.json();
    },
  });

  const stats: ContactStats = statsData ?? { total: 0, withEmail: 0, engaged: 0, buyers: 0, clients: 0, coi: 0, prospects: 0, capitalProviders: 0 };
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  const deleteContactMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/contacts/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to delete contact");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast.success("Contact deleted successfully");
      setDeletingId(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setDeletingId(null);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: deleteContacts,
    onSuccess: ({ deleted, failed }) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setSelectedContacts(previous => new Set([...previous].filter(id => !deleted.includes(id)).concat(failed)));
      if (failed.length) toast.error(`${deleted.length} deleted; ${failed.length} could not be deleted and remain selected. Try again.`);
      else toast.success(`${deleted.length} contacts deleted`);
    },
  });

  const toggleContactSelection = (id: string) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedContacts(newSelected);
  };

  const toggleAllSelection = () => {
    setSelectedContacts(previous => togglePageSelection(previous, filteredContacts.map(contact => contact.id)));
  };

  const handleCopyEmail = (e: React.MouseEvent, email: string, contactId: string) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(email);
    setCopiedEmailId(contactId);
    toast.success("Email copied to clipboard");
    setTimeout(() => setCopiedEmailId(null), 2000);
  };

  const exportToCSV = async (scope: "selected" | "page" | "filtered" = "selected") => {
    setIsExporting(true);
    try {
    const source = scope === "selected"
      ? await loadSelectedContacts<Contact>(Array.from(selectedContacts))
      : scope === "filtered" ? await loadFilteredContacts<Contact>(debouncedSearch, selectedCategory) : contacts;
    const contactsToExport = source.map(contact => ({ ...contact, companyName: contact.companyName || "Unknown", category: contact.companyCategory || "Unknown" }));
    
    const headers = ["Name", "Title", "Email", "Phone", "Company", "Category", "Notes"];
    const rows = contactsToExport.map(c => [
      c.name,
      c.title || "",
      c.email || "",
      c.phone || "",
      c.companyName,
      c.category,
      c.notes || "",
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contacts-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(`${contactsToExport.length} contacts exported to CSV`);
    } catch (error) { toast.error(error instanceof Error ? error.message : "Export failed. Try again."); }
    finally { setIsExporting(false); }
  };

  const initialsOf = (name: string) => name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  const contactsWithCompany = contacts.map(contact => ({
    ...contact,
    companyName: contact.companyName || "Unknown",
    category: contact.companyCategory || "Unknown",
    initials: initialsOf(contact.name),
  }));

  // Search and category are applied by the server; the page is the result.
  const filteredContacts = contactsWithCompany;
  type Row = (typeof filteredContacts)[number];
  const onPageSelected = filteredContacts.filter(c => selectedContacts.has(c.id)).length;

  const contactRowActions = (c: Row): RowAction[] => [
    { label: "Delete", icon: <Trash2 className="w-4 h-4" />, variant: "destructive", onClick: () => setPendingDelete({ id: c.id, name: c.name }) },
  ];

  const columns: Column<Row>[] = [
    {
      id: "name", header: "Name", width: "w-[260px]",
      cell: (c) => (
        <NameCell
          name={c.name}
          after={
            <>
              {(c as { willingToAdvocate?: boolean | null }).willingToAdvocate && <Badge className="text-[11px] px-1.5 py-0 bg-amber-100 text-amber-800">Advocate</Badge>}
              {deletingId === c.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" aria-label="Deleting" />}
            </>
          }
          sub={c.title}
          nameClassName="font-semibold text-foreground"
        />
      ),
    },
    {
      id: "company", header: "Company", width: "w-[220px]",
      cell: (c) => (
        <Link
          href={companyHref(c.companyId!, c.companyCategory)}
          onClick={(e) => e.stopPropagation()}
          title={c.companyName}
          className="block truncate text-foreground hover:text-primary-text hover-underline transition-colors font-medium"
        >
          {c.companyName}
        </Link>
      ),
    },
    {
      id: "category", header: "Category", width: "w-[130px]",
      cell: (c) => (
        <Badge className={`text-[11px] px-1.5 py-0 leading-none ${getCategoryBadgeStyle(c.category)}`}>{c.category}</Badge>
      ),
    },
    {
      id: "email", header: "Email", className: "py-0",
      cell: (c) => c.email ? (
        <span className="flex items-center gap-1 min-w-0">
          <a
            href={`mailto:${c.email}`}
            title={c.email}
            className="flex items-center gap-1.5 min-w-0 text-muted-foreground hover:text-primary-text transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <Mail className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate min-w-0 hover-underline">{c.email}</span>
          </a>
          <button
            onClick={(e) => c.email && handleCopyEmail(e, c.email, c.id)}
            className="shrink-0 p-1 rounded hover:bg-muted transition-colors"
            title="Copy email"
          >
            {copiedEmailId === c.id ? (
              <Check className="w-3 h-3 text-green-500" />
            ) : (
              <Copy className="w-3 h-3 text-muted-foreground" />
            )}
          </button>
        </span>
      ) : <span className="text-muted-foreground/40">{"—"}</span>,
    },
    {
      id: "engaged", header: "Spoken to", width: "w-[80px]", className: "text-center", headerClassName: "text-center",
      cell: (c) => c.engaged ? (
        <span
          className="inline-block"
          title={
            c.lastEngagementTitle
              ? `${c.lastEngagementTitle}${c.lastEngagementAt ? ` (${new Date(c.lastEngagementAt).toLocaleDateString()})` : ""}`
              : "Spoken to"
          }
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" aria-label="Spoken to" />
        </span>
      ) : (
        <span className="text-muted-foreground/40 text-xs" aria-label="No conversation recorded">&ndash;</span>
      ),
    },
  ];

  return (
    <ListShell
      title="All Contacts"
      tiles={<ListTiles tiles={contactTiles(stats)} />}
      toolbar={
        <GridToolbar
          density="compact"
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search contacts..."
          chips={
            <ChartChips
              ariaLabel="Category"
              chips={contactCategoryChips(stats)}
              allCount={stats.total}
              value={selectedCategory === "All Categories" ? ALL_KEY : selectedCategory}
              onChange={(k) => setSelectedCategory(k === ALL_KEY ? "All Categories" : k)}
            />
          }
          actions={
            <>
              <Button variant="outline" size="sm" className="h-8 min-h-0" disabled={isExporting || !pageData || isFetching} onClick={() => void exportToCSV("page")}>Export page</Button>
              <Button variant="outline" size="sm" className="h-8 min-h-0" disabled={isExporting || !pageData || isFetching} onClick={() => void exportToCSV("filtered")}>Export all filtered</Button>
              <Button variant="outline" size="sm" className="h-8 min-h-0" onClick={() => setImportOpen(true)} data-testid="import-contacts-button">
                Import
              </Button>
            </>
          }
          primaryAction={
            <Button
              size="sm"
              onClick={() => setAddContactOpen(true)}
              className="h-8 min-h-0 gap-2 gold-gradient text-primary-foreground shadow-md hover:shadow-lg transition-all font-semibold btn-gold ripple-effect"
              data-testid="add-contact-button"
            >
              <Plus className="w-4 h-4" />
              Add Contact
            </Button>
          }
        />
      }
      above={
        <>
          {contactsError && <QueryError error={contactsError} onRetry={() => void retryContacts()} hasData={!!pageData} busy={isFetching} noun="contacts" />}
          {selectedContacts.size > 0 && (
            <Card className="border border-primary/30 bg-primary/5 shadow-sm">
              <CardContent className="p-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-sm">
                      {selectedContacts.size} selected across pages ({onPageSelected} on this page)
                    </span>
                    <Button variant="outline" size="sm" className="h-8 min-h-0 gap-2" onClick={() => setSelectedContacts(new Set())}>
                      <X className="w-4 h-4" />
                      Clear
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="h-8 min-h-0 gap-2" onClick={() => void exportToCSV("selected")} disabled={isExporting}>
                      {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      Export selected ({selectedContacts.size})
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" className="h-8 min-h-0 gap-2" disabled={bulkDeleteMutation.isPending}>
                          {bulkDeleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {selectedContacts.size} contacts?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently deletes all {selectedContacts.size} selected contacts, including {selectedContacts.size - onPageSelected} outside this page. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => bulkDeleteMutation.mutate(Array.from(selectedContacts))}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      }
      footer={
        <ListPager
          density="compact"
          shown={filteredContacts.length} total={pagination?.total ?? filteredContacts.length} noun={["contact", "contacts"]}
          page={page} totalPages={pagination?.totalPages ?? 1}
          pageSize={pageSize} onPageSizeChange={setPageSize}
          hasPrev={page > 1 && pageSize !== "all"} hasNext={pagination?.hasMore ?? false}
          onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => p + 1)}
          busy={isFetching} updating={isFetching && !contactsLoading} testIdPrefix="contacts"
        />
      }
      after={
        <>
          <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Contact</AlertDialogTitle>
                <AlertDialogDescription>{`Are you sure you want to delete ${pendingDelete?.name ?? "this contact"}? This action cannot be undone.`}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => { if (pendingDelete) { setDeletingId(pendingDelete.id); deleteContactMutation.mutate(pendingDelete.id); } setPendingDelete(null); }}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AddContactModal open={addContactOpen} onOpenChange={setAddContactOpen} />
          {/* Business card scanning moved to COI page (mobile only) */}
          <ImportDialog type="contacts" open={importOpen} onOpenChange={setImportOpen} />
        </>
      }
    >
      {/* Desktop Table View */}
      <div className="hidden md:flex md:flex-col lg:flex-1 lg:min-h-0">
        {contactsError && !pageData ? (
          <p className="p-4 text-sm text-muted-foreground">Contacts unavailable. Use Retry above.</p>
        ) : (
          <AirtableGrid
            density="compact"
            scroll
            data={filteredContacts}
            columns={columns}
            onRowClick={(c) => navigate("/contacts/" + c.id)}
            getRowActions={contactRowActions}
            selectedRows={selectedContacts}
            onSelectRow={(id) => toggleContactSelection(String(id))}
            onSelectAll={() => toggleAllSelection()}
            getRowId={(c) => c.id}
            loading={contactsLoading}
            emptyState={<EmptyState type="contacts" onAction={() => setAddContactOpen(true)} />}
          />
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3 p-3">
        {contactsLoading ? (
          <>
            <MobileContactCardSkeleton />
            <MobileContactCardSkeleton />
            <MobileContactCardSkeleton />
          </>
        ) : contactsError && !pageData ? (<p>Contacts unavailable. Use Retry above.</p>) : filteredContacts.length > 0 ? (
          filteredContacts.map((contact) => (
            <MobileContactCard
              key={contact.id}
              id={contact.id}
              name={contact.name}
              title={contact.title}
              email={contact.email}
              phone={contact.phone}
              companyName={contact.companyName}
              category={contact.category}
              initials={contact.initials}
              isSelected={selectedContacts.has(contact.id)}
              onSelect={() => toggleContactSelection(contact.id)}
              onDelete={() => deleteContactMutation.mutate(contact.id)}
              getCategoryBadgeStyle={getCategoryBadgeStyle}
            />
          ))
        ) : (
          <EmptyState type="contacts" onAction={() => setAddContactOpen(true)} />
        )}
      </div>
    </ListShell>
  );
}
